import React, { useEffect, useState, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { QRStyleConfig } from './QRStyleModal';

interface QRCodeRendererProps {
  data: string;
  size?: number;
  styleConfig?: QRStyleConfig;
  className?: string;
  fallbackUrl?: string;
}

export const QRCodeRenderer: React.FC<QRCodeRendererProps> = ({
  data,
  size = 200,
  styleConfig,
  className = '',
  fallbackUrl
}) => {
  const [dataUrl, setDataUrl] = useState<string>('');
  const [error, setError] = useState(false);
  const qrRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!data) return;

    // Generate QR code client-side
    const generateQR = async () => {
      try {
        // Ensure minimum size to prevent canvas errors
        const minSize = 100;
        const effectiveSize = Math.max(size, minSize);
        
        const qrConfig: any = {
          width: effectiveSize,
          height: effectiveSize,
          data,
          margin: 0,
          qrOptions: {
            errorCorrectionLevel: styleConfig?.errorCorrectionLevel || 'M'
          }
        };

        // Apply style config if provided
        if (styleConfig) {
          qrConfig.dotsOptions = {
            type: styleConfig.dotsType || 'square',
            color: styleConfig.dotsColor,
            gradient: styleConfig.dotsGradient
          };
          qrConfig.cornersSquareOptions = {
            type: styleConfig.cornersSquareType || 'square',
            color: styleConfig.cornersColor || '#000000'
          };
          qrConfig.cornersDotOptions = {
            type: styleConfig.cornersDotType || 'square',
            color: styleConfig.cornersColor || '#000000'
          };
          qrConfig.backgroundOptions = {
            color: styleConfig.backgroundTransparent ? 'transparent' : (styleConfig.backgroundColor || '#FFFFFF')
          };
          
          if (styleConfig.logoUrl) {
            qrConfig.image = styleConfig.logoUrl;
            qrConfig.imageOptions = {
              crossOrigin: 'anonymous',
              margin: styleConfig.logoMargin || 0,
              imageSize: (styleConfig.logoSize || 25) / 100,
              hideBackgroundDots: styleConfig.hideBackgroundDots
            };
          }
        } else {
          // Default styling
          qrConfig.dotsOptions = { type: 'square', color: '#000000' };
          qrConfig.backgroundOptions = { color: '#FFFFFF' };
        }

        const qrCode = new QRCodeStyling(qrConfig);
        qrRef.current = qrCode;
        
        const blob = await qrCode.getRawData('png');
        if (blob) {
          const url = URL.createObjectURL(blob);
          setDataUrl(url);
          setError(false);
        } else {
          throw new Error('Failed to generate QR code');
        }
      } catch (err: any) {
        console.error('Error generating QR code:', err);
        console.error('QR data length:', data.length);
        console.error('Requested size:', size);
        setError(true);
        
        // Try fallback URL if provided
        if (fallbackUrl) {
          setDataUrl(fallbackUrl);
        }
      }
    };

    generateQR();

    // Cleanup
    return () => {
      if (dataUrl && dataUrl.startsWith('blob:')) {
        URL.revokeObjectURL(dataUrl);
      }
    };
  }, [data, size, styleConfig, fallbackUrl]);

  if (!data) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
        <span className="text-gray-400 text-xs">No data</span>
      </div>
    );
  }

  if (error && !dataUrl) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
        <span className="text-gray-400 text-xs">QR Error</span>
      </div>
    );
  }

  return (
    <img 
      src={dataUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjUwIiB5PSI1MCIgc3R5bGU9ImZpbGw6I2FhYTtmb250LXdlaWdodDpib2xkO2ZvbnQtc2l6ZToxMnB4O2ZvbnQtZmFtaWx5OkFyaWFsLEhlbHZldGljYSxzYW5zLXNlcmlmO2RvbWluYW50LWJhc2VsaW5lOmNlbnRyYWwiPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'}
      alt="QR Code"
      className={className}
      style={{ 
        width: size, 
        height: size,
        objectFit: 'contain'
      }}
    />
  );
};